/**
 * FeedbackEventRenderer wrapper that automatically detects the current origin ID
 * based on the current route
 *
 * Maps route paths to origin IDs for proper banner/toast routing
 */

import { FeedbackEventRenderer } from "@pinatagrams/fiesta";

/**
 * Maps routes to their origin IDs for feedback event routing
 * This enables banners and toasts to be properly routed within page context
 */
export const getOriginIdFromPath = (pathname: string): string | undefined => {
	// Remove leading/trailing slashes and trailing params for matching
	const path = pathname.replace(/^\/|\/$/g, "");

	// Map route patterns to origin IDs
	const routeMap: Record<string, string> = {
		"test/notifications": "notifications-test",
		collections: "collections-page",
		"collection/new": "collection-create-page",
		// Match collection/:id routes
		"collection/": "collection-detail-page",
		users: "users-page",
		workflows: "workflows-page",
		profile: "profile-page",
		"print/": "print-page",
	};

	// Try exact match first
	if (routeMap[path]) return routeMap[path];

	// Try pattern matching for routes with dynamic segments
	for (const [pattern, originId] of Object.entries(routeMap)) {
		if (path.startsWith(pattern)) {
			return originId;
		}
	}

	return undefined;
};

/**
 * Renders the FeedbackEventRenderer with the current page's origin ID
 * This component must be placed within the RouterProvider
 *
 * Note: We pass undefined for currentOriginId to disable banner rendering
 * in the body. Banners are rendered manually in the dashboard header instead.
 */
export const FeedbackEventRendererWrapper = () => {
	// Don't pass currentOriginId - we render banners manually in dashboard header
	// This makes FeedbackEventRenderer only handle alerts and toasts
	return <FeedbackEventRenderer currentOriginId={undefined} />;
};

FeedbackEventRendererWrapper.displayName = "FeedbackEventRendererWrapper";
